// ABOUTME: Shared behavior events table component used by EventLog and admin pages
// ABOUTME: Provides consistent table styling with configurable columns and actions

import React from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  TablePagination,
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { BehaviorType } from '../types/api';
import { getBehaviorIcon, getBehaviorLabel, getBehaviorColor, getRecordedByDisplayName } from '../utils/behaviorUtils';
import naluV1Theme from '../theme/theme.nalu-v1';

export interface BehaviorEventEntry {
  behaviorId: number;
  childId: number;
  childName?: string;
  familyName?: string;
  behaviorType: BehaviorType;
  notes?: string;
  recordedAt: string;
  eventTime?: string;
  eventTimezone?: string;
  eventLocalTime?: string;
  recordedByUserName: string;
  recordedByUserId?: number;
  sourceDisplayName?: string;
  source?: string;
}

interface BehaviorEventsTableProps {
  events: BehaviorEventEntry[];
  totalCount?: number;
  page?: number;
  rowsPerPage?: number;
  sortConfig?: { field: string; direction: 'asc' | 'desc' };
  loading?: boolean;
  showChildColumn?: boolean;
  showFamilyColumn?: boolean;
  showEditActions?: boolean;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  onSort?: (field: string) => void;
  onEdit?: (event: BehaviorEventEntry) => void;
  onDelete?: (event: BehaviorEventEntry) => void;
  canEditDelete?: (event: BehaviorEventEntry) => boolean;
}

const BehaviorEventsTable: React.FC<BehaviorEventsTableProps> = ({
  events,
  totalCount = events.length,
  page = 0,
  rowsPerPage = 25,
  sortConfig,
  loading = false,
  showChildColumn = true,
  showFamilyColumn = false,
  showEditActions = false,
  onPageChange,
  onRowsPerPageChange,
  onSort,
  onEdit,
  onDelete,
  canEditDelete,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // ThemeProvider's resolved theme and this module's naluV1Theme import different
  // instances under Vite HMR, so compare a stable identifying field rather than `===`.
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;

  const formatDateTime = (event: BehaviorEventEntry) => {
    // Backend provides eventLocalTime using TimezoneUtilityService
    // Priority order:
    // 1. eventLocalTime (already converted by backend TimezoneUtilityService)
    // 2. eventTime (fallback for older records)
    // 3. recordedAt (fallback for older records)
    
    const timeToUse = event.eventLocalTime || event.eventTime || event.recordedAt;
    
    let weekday, month, day, year, time;
    
    if (event.eventLocalTime && timeToUse === event.eventLocalTime) {
      // Backend provided family timezone time with offset - parse time components directly
      // Example: "2025-08-27T18:20:26.500347-10:00" should display as 6:20 PM
      const isoString = event.eventLocalTime;
      const datePart = isoString.split('T')[0]; // "2025-08-27"
      const timePart = isoString.split('T')[1].split('.')[0]; // "18:20:26"
      
      const [yearStr, monthStr, dayStr] = datePart.split('-');
      const [hourStr, minuteStr] = timePart.split(':');
      
      const dateObj = new Date(parseInt(yearStr), parseInt(monthStr) - 1, parseInt(dayStr));
      weekday = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
      month = parseInt(monthStr);
      day = parseInt(dayStr);
      year = parseInt(yearStr);
      
      // Format time from parsed hour/minute
      const hour24 = parseInt(hourStr);
      const minute = parseInt(minuteStr);
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      const ampm = hour24 >= 12 ? 'PM' : 'AM';
      time = `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
    } else {
      // Fallback case - use Date object formatting
      const date = new Date(timeToUse);
      weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
      month = date.getMonth() + 1;
      day = date.getDate();
      year = date.getFullYear();
      time = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
    
    return `${weekday}, ${month}/${day}/${year} ${time}`;
  };

  const handleSort = (field: string) => {
    if (onSort) {
      onSort(field);
    }
  };

  const paginatedEvents = events;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        Loading behavior events...
      </Box>
    );
  }

  if (events.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', p: 3 }}>
        <Typography variant="body1" color="text.secondary">
          No behavior events found
        </Typography>
      </Box>
    );
  }

  // Mobile card view
  if (isMobile) {
    return (
      <Box>
        {paginatedEvents.map((event) => (
          <Card key={event.behaviorId} sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box>
                  {showChildColumn && (
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {event.childName}
                    </Typography>
                  )}
                  {showFamilyColumn && (
                    <Typography variant="caption" color="text.secondary">
                      {event.familyName}
                    </Typography>
                  )}
                </Box>
                {showEditActions && canEditDelete?.(event) && (
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {onEdit && (
                      <IconButton
                        size="small"
                        onClick={() => onEdit(event)}
                        title="Edit behavior"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    {onDelete && (
                      <IconButton
                        size="small"
                        onClick={() => onDelete(event)}
                        title="Delete behavior"
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                {getBehaviorIcon(event.behaviorType)}
                <Chip
                  label={getBehaviorLabel(event.behaviorType)}
                  size="small"
                  sx={{
                    ml: 1,
                    backgroundColor: getBehaviorColor(event.behaviorType),
                    color: 'white',
                    '& .MuiChip-label': { color: 'white' }
                  }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {formatDateTime(event)}
              </Typography>
              {event.notes && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {event.notes}
                </Typography>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Logged by {getRecordedByDisplayName(event)}
                </Typography>
                <Chip
                  label={event.sourceDisplayName || 'Unknown'}
                  size="small"
                  variant="outlined"
                  color="default"
                  sx={{ fontSize: '0.7rem' }}
                />
              </Box>
            </CardContent>
          </Card>
        ))}
        
        {totalCount > rowsPerPage && onPageChange && onRowsPerPageChange && (
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => onPageChange(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        )}
      </Box>
    );
  }

  // Desktop table view
  return (
    <Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={sortConfig?.field === 'recordedAt'}
                direction={sortConfig?.field === 'recordedAt' ? sortConfig.direction : 'asc'}
                onClick={() => handleSort('recordedAt')}
              >
                Date/Time
              </TableSortLabel>
            </TableCell>
            {showChildColumn && (
              <TableCell>
                <TableSortLabel
                  active={sortConfig?.field === 'childName'}
                  direction={sortConfig?.field === 'childName' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('childName')}
                >
                  Child
                </TableSortLabel>
              </TableCell>
            )}
            {showFamilyColumn && (
              <TableCell>
                <TableSortLabel
                  active={sortConfig?.field === 'familyName'}
                  direction={sortConfig?.field === 'familyName' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('familyName')}
                >
                  Family
                </TableSortLabel>
              </TableCell>
            )}
            <TableCell>Behavior</TableCell>
            <TableCell>Who Logged</TableCell>
            <TableCell>Notes</TableCell>
            <TableCell>Source</TableCell>
            {showEditActions && <TableCell width="10%">Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedEvents.map((event, index) => (
            <TableRow
              key={event.behaviorId}
              sx={{
                bgcolor: isNaluV1Theme
                  ? (index % 2 === 0 ? 'background.paper' : 'background.default')
                  : (index % 2 === 0 ? 'white' : 'grey.50'),
                minHeight: 64
              }}
            >
              <TableCell>{formatDateTime(event)}</TableCell>
              {showChildColumn && <TableCell>{event.childName}</TableCell>}
              {showFamilyColumn && <TableCell>{event.familyName}</TableCell>}
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {getBehaviorIcon(event.behaviorType)}
                  <Chip
                    label={getBehaviorLabel(event.behaviorType)}
                    size="small"
                    sx={{
                      ml: 1,
                      backgroundColor: getBehaviorColor(event.behaviorType),
                      color: 'white',
                      '& .MuiChip-label': { color: 'white' }
                    }}
                  />
                </Box>
              </TableCell>
              <TableCell>{getRecordedByDisplayName(event)}</TableCell>
              <TableCell>{event.notes || '—'}</TableCell>
              <TableCell>
                <Chip
                  label={event.sourceDisplayName || 'Unknown'}
                  size="small"
                  variant="outlined"
                  color="default"
                  sx={{ fontSize: '0.75rem' }}
                />
              </TableCell>
              {showEditActions && (
                <TableCell align="center">
                  {canEditDelete?.(event) && (
                    <Box display="flex" gap={0.5} justifyContent="center">
                      {onEdit && (
                        <IconButton
                          size="small"
                          onClick={() => onEdit(event)}
                          title="Edit behavior"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      {onDelete && (
                        <IconButton
                          size="small"
                          onClick={() => onDelete(event)}
                          title="Delete behavior"
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      
      {totalCount > rowsPerPage && onPageChange && onRowsPerPageChange && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => onPageChange(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      )}
    </Box>
  );
};

export default BehaviorEventsTable;